import './mask-field.scss'

import _ from 'lodash'
import { first } from 'rxjs/operators'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { faEraser, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons'

import { TranslatedString, createTranslatedString, getTranslatedString, EntityField, EntityFieldStatus } from '@skinsoft/types'
import { buildSkinDateWithFormat, DATE_FORMATS as AVAILABLE_DATE_FORMATS, skinDateToInputDate } from '@skinsoft/utils'
import { TranslationDataStore } from '@skinsoft/entity'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { EditableTranslatedStringList, EntitySelect } from '@skinsoft/ui'

declare const DATE_FORMATS: string
const lastId = 1;

@Component({
    components: {
        sEditableTranslatedStringList: EditableTranslatedStringList,
        sEntitySelect: EntitySelect,
    },
    props: ['entityField', 'fieldValue', 'isInMetaMask', 'editing', 'getStatus', 'children', 'dataCy'],
    template: require('./mask-field.html')
})
export class MaskField extends ReactiveVue {

    public entityField: EntityField;
    public fieldValue: any;
    public isInMetaMask: boolean;
    public editing: boolean
    private children: [] | null
    private dataCy: string

    public getStatus: (entityField: EntityField) => EntityFieldStatus

    private entity: any;

    private fieldLabel: string = "";

    private value: any = null;

    private startValue: any = ""

    // Icons
    private eraseIcon = faEraser
    private plusIcon = faPlusCircle;
    private trashIcon = faTrash;

    private singleComplex = ['RealTimeLocation', 'CurrentItemState', 'PermanentLocation']

    private $translationDataStore: TranslationDataStore;

    private fetchLabel(key: string) {
        let translationKey: string = `field.${key}`
        if (this.entityField.entityFields
            && this.entityField.entityFields.length > 0) {
            translationKey += ".complex"
        }
        this.subscribeTo(
            this.$translationDataStore.getMessage(translationKey, this.$i18n.locale).pipe(first()),
            translation => {
                this.fieldLabel = translation
                this.setStartValue()
            },
            'translation-subscription'
        )
    }

    /**
     * Sets the field value according to its corresponding entityField value
     * Adapts the shape if necesseray, according to entityField type
     */
    private setStartValue() {
        switch (this.entityField.type.type) {
            case "TranslatedString":
                if (this.fieldValue) {
                    this.value = getTranslatedString(this.fieldValue, this.$i18n.locale);
                } else {
                    this.value = "";
                }
                break;
            case "SkinDate":
                if (this.fieldValue) {
                    const txtValue = (this.fieldValue.txt) as string
                    this.value = !!txtValue ? txtValue.split('/').reverse().join('-') : skinDateToInputDate(this.fieldValue);
                } else {
                    this.value = null;
                }
                break;
            case "List":
                if (this.fieldValue) {
                    this.value = this.fieldValue
                } else {
                    this.value = [];
                    this.addEntry()
                }
                break;
            default:
                this.value = this.fieldValue
                break
        }
    }

    private thesaurusIdentifierIfNeeded(entityField: EntityField) {
        if (entityField.type.type === "ThesaurusTerm") {
            return entityField.type.thesaurusIdentifier;
        }

        if (entityField.type.entityFieldType?.type === "ThesaurusTerm") {
            return entityField.type.entityFieldType.thesaurusIdentifier
        }

        return null;
    }

    private eraseValue() {
        this.value = null;
    }

    private onEntitySelected(entity: any) {
        this.value = entity.id;
    }

    private onEntitySelectedInList(entity: any, index) {
        if (!this.value) {
            this.value = {}
        }
        this.value[index] = entity.id;
    }

    private updateValue(event: any, index: number, entityFieldName: string) {
        if (!this.value) {
            this.value = {}
        }
        if(this.value[index][entityFieldName] && typeof this.value[index][entityFieldName] === 'object') {
            _.merge(this.value[index][entityFieldName], event.value);
        } else {
            this.value[index][entityFieldName] = event.value
        }
    }

    private updateNonListValue(event: any, entityFieldName: string) {
        if (!this.value) {
            this.value = {}
        }
        this.value[entityFieldName] = event.value
    }

    private needToSpan(entityField: EntityField) {
        return (entityField.type.type === "List" || this.singleComplex.includes(entityField.type.type)) && !entityField.isReadOnly;
    }

    // Methods when value is a list
    private addEntry() {
        if(this.entityField.type.referenceField && this.entityField.type.entityFieldType.type === "ThesaurusTerm") {
            this.value.push('')
        } else {
            this.value.push({})
        }
    }

    private removeEntry(index: number) {
        this.value.splice(index, 1)
    }

    private isReadonly(entityField: EntityField) {
        return entityField.isReadOnly || this.getStatus(entityField).disabled
    }

    private hasError(entityField: EntityField) {
        return this.getStatus(entityField).error
    }

    private infoMessage(entityField: EntityField): string | undefined {
        return this.getStatus(entityField).disabledMessage
    }

    private errorMessage(entityField: EntityField): string | undefined {
        return this.getStatus(entityField).errorMessage
    }

    private getTranslatedString(translatedString: TranslatedString) {
        return getTranslatedString(translatedString, this.$i18n.locale);
    }

    /**
     * Reshapes value if necessary before sending it to backend
     */
    private adaptValueAndEmit() {
        let returnValue = this.value;
        switch (this.entityField.type.type) {
            case "TranslatedString":
                if (this.value) {
                    returnValue = createTranslatedString(this.$i18n.locale, this.value);
                } else {
                    this.value = null;
                }
                break;
            case "SkinDate":
                if (this.value) {
                    const reshapedDateTxt = this.value.split('-').reverse().join('/')
                    returnValue = buildSkinDateWithFormat(reshapedDateTxt, AVAILABLE_DATE_FORMATS.DD_MM_YYYY_SLASH);
                } else {
                    returnValue = null
                }
                break;
            default:
                returnValue = this.value;
                break
        }
        this.$emit("value-change", {
            abstractEntity: this.entityField.abstractEntity,
            fieldPath: this.entityField.path,
            value: returnValue,
        })
    }

    private getChildStatus(childEntityField: EntityField): EntityFieldStatus {
        const parentStatus = this.getStatus(this.entityField)
        let childStatus = this.getStatus(childEntityField)
        if(parentStatus.disabled) {
            childStatus.disabled = true
        }
        return childStatus
    }

    private get getDataCy(): string {
        if(this.dataCy) {
            return this.dataCy
        }

        if(this.entityField?.type?.type) {
            switch(this.entityField.type.type) {
                case 'TranslatedString':
                case 'String':
                    return 'mask-field--text'
                case 'Boolean':
                    return 'mask-field--bool'
                case 'SkinDate':
                    return 'mask-field--date'
            }
        }

        return 'mask-field'
    }

    private getDateFormatOptions() {
        // for the moment, the 'locale' prop of BDatePicker override the order of the different fields (mm,DD,yyyy)
        switch(DATE_FORMATS) {
            case AVAILABLE_DATE_FORMATS.DD_MM_YYYY_SLASH:
                return {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }
            case AVAILABLE_DATE_FORMATS.MM_DD_YYYY_SLASH:
                return {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                }
            case AVAILABLE_DATE_FORMATS.YYYY_MM_DD_SLASH: 
                return {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }
            case AVAILABLE_DATE_FORMATS.DD_MM_YYYY_DASH:
                return {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }
            case AVAILABLE_DATE_FORMATS.MM_DD_YYYY_DASH:
                return {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                }
            case AVAILABLE_DATE_FORMATS.YYYY_MM_DD_DASH: 
                return {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }
            default: 
                return {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }
        }
    }

    @Watch('value', { deep: true })
    private onValueChange() {
        this.adaptValueAndEmit()
    }

    @Watch('entityField', { immediate: true, deep: false })
    private onEntityFieldChange() {
        this.unsubscribeFrom('translation-subscription')
        this.fetchLabel(this.entityField.path);
    }

    @Watch('fieldValue')
    private onFieldLabelChange() {
        this.setStartValue()
    }

}